import { Box, Typography } from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import { tomorrowDate, tomorrowDateTimeLocal } from '../dateUtils';
import type { DefinitionFormData } from '../CampaignDefinitionStep';
import DeferredTextField from './DeferredTextField';
import { definitionErrors } from '../validation/fieldErrors';

interface ScheduleFieldsProps {
  data: DefinitionFormData;
  onChange: (patch: Partial<DefinitionFormData>) => void;
}

const toPostDate = (date: string) => (date ? `${date}T00:00` : '');

function ScheduleFields({ data, onChange }: ScheduleFieldsProps) {
  const { t } = useLanguage();
  const errors = definitionErrors(data);
  const fieldError = (name: keyof typeof errors) => ({ error: Boolean(errors[name]), helperText: errors[name] });

  if (data.channel === 'cashdesk') {
    if (data.isPreEdited) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Offre pré-éditée : pas de dates d&apos;édition.
        </Typography>
      );
    }
    return (
      <>
        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>
          Dates d&apos;édition
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, maxWidth: 560 }}>
          <DeferredTextField
            label="Date et heure de début"
            type="datetime-local"
            value={data.editionDateStart}
            suggestion={tomorrowDateTimeLocal}
            onCommit={editionDateStart => onChange({ editionDateStart })}
            {...fieldError('editionDateStart')}
            disabled={data.isRecurring}
          />
          <DeferredTextField
            label="Date et heure de fin"
            type="datetime-local"
            value={data.editionDateEnd}
            suggestion={tomorrowDateTimeLocal}
            onCommit={editionDateEnd => onChange({ editionDateEnd })}
            {...fieldError('editionDateEnd')}
            disabled={data.isRecurring}
          />
        </Box>
      </>
    );
  }

  if (data.channel === 'courrier') {
    return (
      <DeferredTextField
        key="post-date"
        label="Date de dépôt poste"
        type="date"
        value={data.sendDate.slice(0, 10)}
        suggestion={tomorrowDate}
        onCommit={date => onChange({ sendDate: toPostDate(date) })}
        {...fieldError('sendDate')}
        disabled={data.isRecurring}
        sx={{ maxWidth: 360 }}
      />
    );
  }

  return (
    <DeferredTextField
      key="send-date"
      label={t('pages.marketing.communication.sendDate')}
      type="datetime-local"
      value={data.sendDate}
      suggestion={tomorrowDateTimeLocal}
      onCommit={sendDate => onChange({ sendDate })}
      {...fieldError('sendDate')}
      disabled={data.isRecurring}
      sx={{ maxWidth: 360 }}
    />
  );
}

export default ScheduleFields;
